import { useGlobalPsManagementStore } from '@/store/ps-management/global-payment-methods.store';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, defineComponent, ref } from 'vue';
import { EditPen, QuestionFilled, Key, Setting } from '@element-plus/icons-vue';
import {
  GlobalPaymentMethodColumnProperties,
  GlobalPaymentMethodItem,
  GlobalPaymentMethodPartialUpdateParameterKeyModel,
  GlobalPaymentMethodPartialUpdateParams,
  globalPaymentMethodColumnsList,
  GlobalDropdownActionCommand,
} from '@/models/ps-management/ps-management.model';
import PsLimitsDetails from '@/layouts/ps-management-page/ps-limits-details/ps-limits-details.vue';
import { usePermissionsStore } from '@/store/permissions.store';
import { PS_MANAGEMENT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import { ElMessageBox } from 'element-plus';
import { usePaymentTypeListStore } from '@/store/dictionary/payment-type-list.store';
import { TagComponentTypes } from '@/models/app-common.model';
import { usePaymentMethodsExceptionLimitStore } from '@/store/ps-management/payment-method-exception-limits.store';
import { PaymentMethodExceptionLimitType } from '@/models/ps-management/exception-limit.model';
import { usePaymentMethodCurrencyConverterListStore } from '@/store/ps-management/global-currency-converter-list.store';
import FallbackImage from '@/components/fallback-image/fallback-image.vue';

const icons = {
  EditPen,
  QuestionFilled,
  Key,
  Setting,
};

export default defineComponent({
  name: 'GlobalPaymentMethodsList',
  components: {
    PsLimitsDetails,
    FallbackImage,
    AddSecretKeySidebar: defineAsyncComponent(
      () => import('@/layouts/ps-management-page/add-secret-key-sidebar/add-secret-key-sidebar.vue'),
    ),
  },
  setup() {
    const globalPsManagementStore = useGlobalPsManagementStore();
    const {
      partialUpdateGlobalPaymentMethod,
      openGeneralPaymentMethodSettings,
    } = globalPsManagementStore;
    const {
      globalPaymentMethodList,
      isGlobalPaymentMethodListLoading,
    } = storeToRefs(globalPsManagementStore);

    const updatePaymentMethodSwitch = (payload: GlobalPaymentMethodPartialUpdateParams) => {
      partialUpdateGlobalPaymentMethod(payload);
    };

    const { psManagementPermissions } = storeToRefs(usePermissionsStore());

    const { openExceptionLimitSettings } = usePaymentMethodsExceptionLimitStore();

    const { openCurrencyConverterSettings } = usePaymentMethodCurrencyConverterListStore();

    const { getPaymentTypeLabel } = usePaymentTypeListStore();

    const visibleDrawer = ref(false);
    const selectedPaymentMethod = ref<GlobalPaymentMethodItem | null>(null);

    const switchPaymentMethod = (
      currentRow: GlobalPaymentMethodItem,
      currentColumn: GlobalPaymentMethodPartialUpdateParameterKeyModel,
    ) => {
      ElMessageBox.confirm(
        'Are you sure you want to toggle the PS for the casino?',
        'Warning',
        {
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          type: TagComponentTypes.WARNING,
        },
      )
        .then(() => {
          updatePaymentMethodSwitch({
            parameterKey: currentColumn,
            billingPaymentMethodId: currentRow.billingPaymentMethodId,
            newValue: currentRow[currentColumn],
          });
        })
        .catch(() => {
          const globalPaymentMethod = globalPaymentMethodList.value
            .find((globalPaymentMethod) =>
              globalPaymentMethod.billingPaymentMethodId === currentRow.billingPaymentMethodId);

          if (!globalPaymentMethod) {
            return;
          }

          globalPaymentMethod[currentColumn] = !currentRow[currentColumn];
        });
    };

    const addSecretKeyLabel = (secretsAdded: GlobalPaymentMethodItem['secretsAdded']) => {
      return secretsAdded ? 'Add new secret key' : 'Add secret key';
    };

    const visibleAddSecretKeyDrawer = (row: GlobalPaymentMethodItem) => {
      selectedPaymentMethod.value = row;

      visibleDrawer.value = !visibleDrawer.value;
    };

    const getDropdownActionCommand = (
      command: { action: GlobalDropdownActionCommand; operationType: PaymentMethodExceptionLimitType },
      row: GlobalPaymentMethodItem,
    ) => {
      const { action, operationType } = command;

      const actionMap: Record<GlobalDropdownActionCommand, () => void> = {
        [GlobalDropdownActionCommand.OPEN_EDIT_PAYMENT_METHOD]: () =>
          openGeneralPaymentMethodSettings(row.billingPaymentMethodId),
        [GlobalDropdownActionCommand.OPEN_ADD_SECRET_KEY]: () => visibleAddSecretKeyDrawer(row),
        [GlobalDropdownActionCommand.OPEN_EXCEPTION_LIMIT_SETTINGS]:
          () => openExceptionLimitSettings(row, operationType),
        [GlobalDropdownActionCommand.OPEN_INTERNAL_CONVERTER_SETTINGS]: () => openCurrencyConverterSettings(row),
      };

      actionMap[action]();
    };

    const formatCurrencyConverter = (globalPaymentMethodItem: GlobalPaymentMethodItem): string[] => {
      if (!globalPaymentMethodItem.isCurrencyConversionAvailable) {
        return ['Unavailable'];
      }

      const currencyConverterEntries = Object.entries(globalPaymentMethodItem.currencyConverterList ?? {});

      if (currencyConverterEntries.length === 0) {
        return [];
      }

      return currencyConverterEntries.map(([to, fromList]: [string, string[]]) => `${fromList.join(', ')} → ${to}`);
    };

    const isUpdateGlobalPaymentMethodAllowed = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GLOBAL_PAYMENT_METHOD],
    );

    const isPartialUpdateGlobalPaymentMethodAllowed = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GLOBAL_PAYMENT_METHOD_DEPOSIT_AND_WITHDRAW],
    );

    const isUpdateExceptionLimitAllowed = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_DEPOSIT_LIMITS],
    );

    const isUpdateConverterSettingsAllowed = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.GET_PAYMENT_METHOD_CURRENCY_CONVERTER_LIST],
    );

    return {
      icons,
      isGlobalPaymentMethodListLoading,
      globalPaymentMethodList,
      globalPaymentMethodColumnsList,
      GlobalPaymentMethodColumnProperties,
      updatePaymentMethodSwitch,
      openGeneralPaymentMethodSettings,
      isUpdateGlobalPaymentMethodAllowed,
      isPartialUpdateGlobalPaymentMethodAllowed,
      switchPaymentMethod,
      getPaymentTypeLabel,
      addSecretKeyLabel,
      getDropdownActionCommand,
      GlobalDropdownActionCommand,
      visibleDrawer,
      selectedPaymentMethod,
      isUpdateExceptionLimitAllowed,
      PaymentMethodExceptionLimitType,
      isUpdateConverterSettingsAllowed,
      formatCurrencyConverter,
    };
  },
});
